import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, checking, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF4311]" /></div>;
  }
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-4">
      <div className="jpd-card w-full max-w-md p-8 sm:p-10">
        <div className="flex items-center gap-2.5">
          <span className="w-10 h-10  bg-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </span>
          <div>
            <p className="font-display font-bold leading-tight">BRAD'S <span className="text-[#FF4311]">DETAILING</span></p>
            <p className="text-xs text-slate-500">Owner Dashboard</p>
          </div>
        </div>

        <h1 className="mt-7 text-xl sm:text-2xl font-semibold flex items-center gap-2">
          <Lock className="w-5 h-5 text-[#FF4311]" /> Sign in
        </h1>

        <form data-testid="admin-login-form" onSubmit={submit} className="mt-6 space-y-4">
          <input
            data-testid="admin-login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="jpd-input"
            autoComplete="email"
          />
          <input
            data-testid="admin-login-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="jpd-input"
            autoComplete="current-password"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30  px-4 py-2.5">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            disabled={loading}
            className="w-full bg-primary hover:bg-[#C20A0B] disabled:opacity-60 text-white font-semibold py-3.5  transition-colors inline-flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div className="mt-5 flex items-center justify-between text-sm">
          <Link to="/forgot-password" data-testid="admin-forgot-password-link" className="text-[#FF4311] hover:underline">
            Forgot password?
          </Link>
          <Link to="/owner-login" data-testid="admin-login-join-link" className="text-slate-400 hover:text-white transition-colors">
            Owner Login
          </Link>
          <Link to="/" data-testid="admin-login-home-link" className="text-slate-400 hover:text-white transition-colors">
            Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}
